<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head">
        <h4>سجل أحداث الأجهزة ({{ logs.total ?? 0 }})</h4>
      </div>
      <div class="row mb-3">
        <div class="col-lg-6 col-md-12 col-12">
          <div class="form-group">
            <label for="word">ابحث</label>
            <input type="search" id="word" class="form-control" placeholder="رقم الحجز / اسم الطالب"
                   v-model="word" @keyup="debounceFetch" />
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-12">
          <div class="form-group d-flex flex-column">
            <label>الإجراء</label>
            <Dropdown v-model="action" :options="actionOptions" showClear optionLabel="title"
                      placeholder="كل الإجراءات" @change="fetchLogs(1)" />
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>التاريخ والوقت</th>
            <th>رقم الحجز</th>
            <th>الطالب</th>
            <th>الإجراء</th>
            <th>المسؤول</th>
            <th>السبب</th>
            <th>السبب التفصيلي</th>
            <th>الحالة السابقة</th>
            <th>الحالة الجديدة</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in logs.data" :key="h.id">
            <td class="text-nowrap">{{ h.created_at }}</td>
            <td>{{ h.reservation_number ?? '-' }}</td>
            <td>{{ h.student_name ?? '-' }}</td>
            <td>
              <span class="status-badge" :class="`status-badge--${actionColor(h.action)}`">
                <i class="pi" :class="actionIcon(h.action)"></i>
                {{ actionLabel(h) }}
              </span>
            </td>
            <td>{{ h.admin ?? 'النظام' }}</td>
            <td>{{ reasonLabel(h.reason) }}</td>
            <td>{{ h.custom_reason ?? '-' }}</td>
            <td>{{ h.previous_status ?? (h.previous_app_version ? `v${h.previous_app_version}` : '-') }}</td>
            <td>{{ h.new_status ?? (h.new_app_version ? `v${h.new_app_version}` : '-') }}</td>
            <td>{{ h.ip_address ?? '-' }}</td>
          </tr>
          <tr v-if="!logs.data || logs.data.length === 0">
            <td colspan="10" class="text-center py-4">لا يوجد سجل بعد</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row" v-if="logs.total">
      <div class="col-12">
        <pagination :data="logs" class="mx-auto d-flex align-items-center justify-content-center pagination"
                    @pagination-change-page="fetchLogs">
          <template v-slot:prev-nav>&lt;</template>
          <template v-slot:next-nav>&gt;</template>
        </pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

const ACTION_LABELS = {
  device_registered: 'تسجيل جهاز جديد',
  login: 'تسجيل الدخول',
  suspend: 'تعليق الجهاز',
  reactivate: 'إعادة تفعيل الجهاز',
  reset: 'إعادة ضبط الجهاز',
  change_approved: 'اعتماد طلب تغيير الجهاز',
  change_rejected: 'رفض طلب تغيير الجهاز',
  app_updated: 'تحديث التطبيق',
  force_logout: 'تسجيل خروج إجباري',
};

const ACTION_ICONS = {
  device_registered: 'pi-plus-circle',
  login: 'pi-sign-in',
  suspend: 'pi-pause-circle',
  reactivate: 'pi-play-circle',
  reset: 'pi-refresh',
  change_approved: 'pi-sync',
  change_rejected: 'pi-times-circle',
  app_updated: 'pi-arrow-up-right',
  force_logout: 'pi-sign-out',
};

// Reuses the status-badge--{active,suspended,pending,accepted,rejected,waiting}
// palette already defined globally (design-system.css) — blue/orange map to
// the closest existing tones rather than adding new CSS variants.
const ACTION_COLORS = {
  device_registered: 'accepted',
  login: 'active',
  suspend: 'pending',
  reactivate: 'active',
  reset: 'rejected',
  change_approved: 'accepted',
  change_rejected: 'rejected',
  app_updated: 'waiting',
  force_logout: 'pending',
};

export default {
  name: 'DeviceAuditLogs',
  components: { Breadcrumb, Dropdown, pagination },
  data() {
    return {
      logs: { data: [], total: 0 },
      word: '',
      action: null,
      actionOptions: Object.entries(ACTION_LABELS).map(([id, title]) => ({ id, title })),
      searchTimeout: null,
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'الأجهزة المسجلة', path: '/students/devices' },
        { name: 'سجل أحداث الأجهزة', path: '/students/device-audit-logs' },
      ],
    };
  },
  methods: {
    actionLabel(h) {
      return ACTION_LABELS[h.action] ?? h.action;
    },
    actionIcon(action) {
      return ACTION_ICONS[action] ?? 'pi-circle';
    },
    actionColor(action) {
      return ACTION_COLORS[action] ?? 'blocked';
    },
    reasonLabel(reason) {
      if (!reason) return '-';
      const map = {
        account_sharing: 'مشاركة الحساب',
        lost_phone: 'فقدان الهاتف',
        suspicious_activity: 'نشاط مشبوه',
        temporary_suspension: 'تعليق مؤقت',
        device_under_investigation: 'الجهاز قيد التحقيق',
        student_requested_new_phone: 'الطالب طلب هاتفاً جديداً',
        phone_replaced: 'تم استبدال الهاتف',
        phone_sold: 'تم بيع الهاتف',
        factory_reset: 'تمت إعادة ضبط المصنع',
        damaged_device: 'الجهاز تالف',
        other: 'أخرى',
      };
      return map[reason] ?? reason;
    },
    debounceFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchLogs(1), 600);
    },
    fetchLogs(page = 1) {
      const params = { page };
      if (this.word) params.word = this.word;
      if (this.action) params.action = this.action.id;

      axios.get('/devices/audit-logs', { params }).then(({ data }) => {
        this.logs = data.data;
      }).catch(() => {
        this.logs = { data: [], total: 0 };
      });
    },
  },
  mounted() {
    this.fetchLogs();
  },
};
</script>
